import { z } from "zod";
import { domainSignInModeInputSchema, domainSignInModeSchema } from "./auth.ts";
import { entityIdSchema, uuidSchema } from "./tenancy.ts";

/** A setting write at one level; `locked` stops every level below from overriding it. */
export const putSettingRequestSchema = z.object({
  value: z.unknown(),
  locked: z.boolean().default(false),
  /**
   * For a tenant's `models.labs` (model-picking D4, D17): the admin confirmed the "Not tested by
   * ESI" warning for the untested labs the write adds; required for them (else
   * `422 untested_lab_unacknowledged`).
   */
  acknowledgeUntested: z.boolean().optional(),
});
export type PutSettingRequest = z.infer<typeof putSettingRequestSchema>;

export const resolvedSettingSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  /** Where the effective value comes from. */
  source: z.enum(["instance", "tenant", "entity", "project"]),
  sourceEntityId: entityIdSchema.nullable(),
  /** True when a level above locks it, so it cannot be changed at this level. */
  locked: z.boolean(),
});
export type ResolvedSettingResponse = z.infer<typeof resolvedSettingSchema>;

/**
 * A project setting: the effective resolution plus whether a project may override the key
 * (`narrowOnly`: only with a stricter value) and the value it inherits without its override.
 */
export const projectSettingSchema = resolvedSettingSchema.extend({
  overridable: z.enum(["no", "yes", "narrowOnly"]),
  inherited: z.object({
    value: z.unknown(),
    source: z.enum(["instance", "tenant", "entity", "project"]),
    sourceEntityId: entityIdSchema.nullable(),
  }),
  /** Why the key has no effect on this instance (e.g. its tool is not deployed), if so. */
  unavailable: z.string().optional(),
  /**
   * `wiki.trust` only (wiki-collaboration D9): how many direct members may publish, so the row
   * warns that `peer` would block publication below two. Absent from an N-1 API.
   */
  publishers: z.int().min(0).optional(),
});
export type ProjectSettingResponse = z.infer<typeof projectSettingSchema>;

/** Identity provider presets: a known vendor whose issuer and scopes ESI fixes. */
export const identityProviderPresetSchema = z.enum(["google_workspace"]);
export type IdentityProviderPreset = z.infer<typeof identityProviderPresetSchema>;

/** Google's OIDC issuer and the scopes of the `google_workspace` preset (openspec google-workspace D1). */
export const GOOGLE_ISSUER = "https://accounts.google.com";
export const GOOGLE_WORKSPACE_SCOPES = ["openid", "email", "profile"] as const;

const idpDisplayNameSchema = z.string().trim().min(1).max(100);

/**
 * The Google Workspace preset: the client of an OAuth client in the customer's Google Cloud
 * project. The issuer and scopes are fixed server-side; any the client sends are dropped.
 */
export const googleWorkspaceIdpRequestSchema = z.object({
  protocol: z.literal("oidc"),
  preset: z.literal("google_workspace"),
  displayName: idpDisplayNameSchema,
  clientId: z.string().min(1),
  /** Forwarded to the authorization server, never stored by ESI. */
  clientSecret: z.string().min(1),
});

export const oidcIdpRequestSchema = z.object({
  protocol: z.literal("oidc"),
  displayName: idpDisplayNameSchema,
  issuer: z.url({ protocol: /^https$/ }),
  clientId: z.string().min(1),
  /** Forwarded to the authorization server, never stored by ESI. */
  clientSecret: z.string().min(1),
  scopes: z.array(z.string()).default(["openid", "email", "profile"]),
});

export const samlIdpRequestSchema = z.object({
  protocol: z.literal("saml"),
  displayName: idpDisplayNameSchema,
  metadataUrl: z.url({ protocol: /^https$/ }),
});

/** A preset first: a body naming one is never read as a generic OIDC IdP. */
export const identityProviderRequestSchema = z.union([
  googleWorkspaceIdpRequestSchema,
  oidcIdpRequestSchema,
  samlIdpRequestSchema,
]);
export type IdentityProviderRequest = z.infer<typeof identityProviderRequestSchema>;

/** What the authorization server is configured with: a preset resolved to its issuer. */
export type FederationConfig =
  | z.infer<typeof oidcIdpRequestSchema>
  | z.infer<typeof samlIdpRequestSchema>;

/** Resolves a preset to the concrete federation it stands for. */
export function federationConfig(input: IdentityProviderRequest): FederationConfig {
  if ("preset" in input) {
    return {
      protocol: "oidc",
      displayName: input.displayName,
      issuer: GOOGLE_ISSUER,
      clientId: input.clientId,
      clientSecret: input.clientSecret,
      scopes: [...GOOGLE_WORKSPACE_SCOPES],
    };
  }
  return input;
}

export const domainRequestSchema = z.object({
  domain: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/),
  identityProviderId: uuidSchema.nullable().default(null),
});

/**
 * Removing an IdP or releasing a verified domain (idp-domain-removal D8): the IdP's display name
 * or the domain, typed again. An unverified claim's release needs none (an empty body is fine).
 */
export const removalConfirmationSchema = z.object({
  confirm: z.string().max(300).optional(),
});
export type RemovalConfirmation = z.infer<typeof removalConfirmationSchema>;

/** What a removal or release ends, for its confirmation (counts only, no names or e-mails). */
export const removalImpactSchema = z.object({
  people: z.int(),
  createdAtFirstSignIn: z.int(),
  sessions: z.int(),
  grants: z.int(),
  domains: z.array(z.string()),
  directorySources: z.array(z.object({ name: z.string(), groups: z.int(), memberships: z.int() })),
  /** The acting admin is among the people signed out. */
  signsOutActor: z.boolean(),
  /**
   * A release whose IdP stays and may still vouch for the domain's e-mails (open scope): its
   * display name. Its users are signed out but can sign in through it again, so `people` (who
   * lose sign-in) leaves them out and `signedOut` counts them. Absent otherwise.
   */
  signInContinues: z.object({ identityProvider: z.string(), signedOut: z.int() }).optional(),
  /**
   * An IdP removal (#419, D11): each verified domain bound to it, which the admin may keep as an
   * ESI-accounts domain instead of releasing it. Counts of the people linked through the IdP on
   * it: those holding an ESI credential (they keep it) and the active ones without one (each
   * gets a set-password link). `kept`: chosen in this preview (`?keep=`). Absent from releases
   * and older releases.
   */
  keepableDomains: z
    .array(
      z.object({
        id: z.string(),
        domain: z.string(),
        withCredential: z.int(),
        withoutCredential: z.int(),
        kept: z.boolean(),
      }),
    )
    .optional(),
  /** The tenant's `auth.mfa`, which ESI sign-in on a kept domain then follows (#419, D11). */
  keptDomainMfa: z.enum(["required", "optional"]).optional(),
  /** The last-sign-in-method guard allows it; `refusal` says why not. */
  allowed: z.boolean(),
  refusal: z.enum(["last_sign_in_method"]).optional(),
});
export type RemovalImpact = z.infer<typeof removalImpactSchema>;

/** The verified domains of an IdP removal kept as ESI-accounts domains (#419, D11), by ID. */
const keepDomainsSchema = z.array(uuidSchema).max(100);

/** `DELETE /api/admin/identity-providers/:id`: the confirmation and the domains kept. */
export const idpRemovalRequestSchema = removalConfirmationSchema.extend({
  keepDomains: keepDomainsSchema.optional(),
});
export type IdpRemovalRequest = z.infer<typeof idpRemovalRequestSchema>;

/** `GET /api/admin/identity-providers/:id/removal?keep=<id>,<id>`: the preview of that choice. */
export const idpRemovalPreviewQuerySchema = z.object({
  keep: z
    .string()
    .max(4000)
    .optional()
    .transform((v) => (v ? v.split(",").filter(Boolean) : []))
    .pipe(keepDomainsSchema),
});

/** What an IdP removal did: the broker clean-up, and the set-password links of kept domains. */
export const idpRemovalResultSchema = z.object({
  cleanupPending: z.boolean(),
  /** Links e-mailed to people on kept domains, and those that could not be (#419, D11). */
  linksSent: z.int().optional(),
  linksFailed: z.int().optional(),
});
export type IdpRemovalResult = z.infer<typeof idpRemovalResultSchema>;

/** `PUT /api/admin/domains/:domainId/sign-in-mode` (#419, D1). */
/** `sso_only` (the previous release's dialog) is accepted for one release as "SSO only" (#438). */
export const domainSignInModeRequestSchema = z.object({ mode: domainSignInModeInputSchema });
export const domainSignInModeQuerySchema = z.object({ mode: domainSignInModeInputSchema });

/**
 * What a domain's mode change would do (#419, D7, D8), counts only: the people whose ESI
 * credential stops being allowed, their password and passkey sessions and the grants those
 * opened; whether the guard and the rules allow it, and why not.
 */
export const domainSignInModePreviewSchema = z.object({
  mode: domainSignInModeSchema,
  /** Always `mode` now (#438, no instance cap); kept for the previous release's SPA. */
  effectiveMode: domainSignInModeSchema,
  people: z.int(),
  sessions: z.int(),
  grants: z.int(),
  allowed: z.boolean(),
  refusal: z.enum(["last_sign_in_method"]).optional(),
});
export type DomainSignInModePreview = z.infer<typeof domainSignInModePreviewSchema>;

export const domainSignInModeResultSchema = z.object({
  mode: domainSignInModeSchema,
  effectiveMode: domainSignInModeSchema,
  people: z.int(),
  sessions: z.int(),
});

export const identityProviderRemovalResultSchema = z.object({
  /** The broker's clean-up did not finish: repeating the removal finishes it. */
  cleanupPending: z.boolean(),
});

export const auditQuerySchema = z.object({
  action: z.string().optional(),
  /** Actions starting with this, e.g. `memory.` for the memory screen (memory-governance). */
  actionPrefix: z
    .string()
    .regex(/^[a-z][a-z_.]{0,63}$/)
    .optional(),
  entityId: uuidSchema.optional(),
  /** Events about one target, e.g. a project's activity (`project` + its ID). */
  targetType: z.string().max(64).optional(),
  targetId: z.string().max(200).optional(),
  /**
   * Events about any of these targets, comma-separated (#84): the memory screen reads its three
   * settings' changes in one query.
   */
  targetIds: z
    .string()
    .max(2_000)
    .transform((v) => v.split(","))
    .pipe(z.array(z.string().min(1).max(200)).max(20))
    .optional(),
  /** Actions to leave out (applied before `limit`). */
  excludeActions: z.array(z.string().max(100)).max(20).optional(),
  before: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export const auditEventSchema = z.object({
  id: z.number(),
  occurredAt: z.iso.datetime(),
  actorType: z.enum(["user", "operator", "system"]),
  actorId: z.string(),
  entityId: z.string().nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  beforeHash: z.string().nullable(),
  afterHash: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
});
export type AuditEventResponse = z.infer<typeof auditEventSchema>;
